

import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaUserTie, FaCalendarAlt, FaEnvelope, FaPhone } from 'react-icons/fa';
import { update } from 'firebase/database';
import DetailModal from '../Incharge/DetailModal';

const EngineerRegistrations = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const registrationsRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubData = onValue(registrationsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                setRegistrations(list.reverse());
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const handleStatusUpdate = async (id, updateData) => {
        const itemRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
        await update(itemRef, updateData);
    };

    const filteredData = registrations.filter(item => {
        const fullName = `${item.firstName || ''} ${item.middleName || ''} ${item.surname || ''}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
    });

    const getStatusStyle = (status) => {
        switch(status) {
            case 'accepted':
                return {
                    bg: 'bg-emerald-50',
                    text: 'text-emerald-700',
                    border: 'border-emerald-200',
                    icon: '✓'
                };
            case 'rejected':
                return {
                    bg: 'bg-rose-50',
                    text: 'text-rose-700',
                    border: 'border-rose-200',
                    icon: '✗'
                };
            default:
                return {
                    bg: 'bg-amber-50',
                    text: 'text-amber-700',
                    border: 'border-amber-200',
                    icon: '⏳'
                };
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
                <div className="relative">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-8 w-8 bg-blue-600 rounded-full animate-pulse"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 p-4 md:p-8 overflow-auto">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-lg border border-slate-100">
                        <div className="space-y-1">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                                    <FaUserTie className="text-white text-2xl" />
                                </div>
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
                                        सुशिक्षित बेरोजगार अभियंता नोंदणी
                                    </h1>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 bg-slate-50 px-6 py-3 rounded-xl">
                            <span className="text-sm font-medium text-slate-600">एकूण नोंदणी:</span>
                            <span className="text-2xl font-bold text-blue-600">{filteredData.length}</span>
                        </div>
                    </div>
                </div>

                {/* Search Section */}
                <div className="mb-6 group">
                    <div className="bg-white p-1 rounded-2xl shadow-lg border border-slate-100">
                        <div className="relative">
                            <FaSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                            <input 
                                type="text" 
                                placeholder="नाव शोधा (उदा. राम शर्मा)..."
                                className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-transparent focus:border-blue-500 focus:ring-0 outline-none text-slate-700 placeholder-slate-400 bg-slate-50"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-xl shadow-md border border-slate-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500 mb-1">प्रलंबित</p>
                                <p className="text-2xl font-bold text-amber-600">{filteredData.filter(d => !d.status || d.status === 'pending').length}</p>
                            </div>
                            <div className="p-3 bg-amber-50 rounded-lg">
                                <span className="text-amber-600 text-xl">⏳</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-md border border-slate-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500 mb-1">स्वीकारले</p>
                                <p className="text-2xl font-bold text-emerald-600">{filteredData.filter(d => d.status === 'accepted').length}</p>
                            </div>
                            <div className="p-3 bg-emerald-50 rounded-lg">
                                <span className="text-emerald-600 text-xl">✓</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-md border border-slate-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500 mb-1">नाकारले</p>
                                <p className="text-2xl font-bold text-rose-600">{filteredData.filter(d => d.status === 'rejected').length}</p>
                            </div>
                            <div className="p-3 bg-rose-50 rounded-lg">
                                <span className="text-rose-600 text-xl">✗</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                                    <th className="px-6 py-4 text-sm font-semibold text-slate-700">नाव</th>
                                    <th className="px-6 py-4 text-sm font-semibold text-slate-700">तालुका</th>
                                    <th className="px-6 py-4 text-sm font-semibold text-slate-700">मोबाईल</th>
                                    <th className="px-6 py-4 text-sm font-semibold text-slate-700">पात्रता</th>
                                    <th className="px-6 py-4 text-sm font-semibold text-slate-700">दिनांक</th>
                                    <th className="px-6 py-4 text-sm font-semibold text-slate-700">स्थिती</th>
                                    <th className="px-6 py-4 text-sm font-semibold text-slate-700">कृती</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredData.map((item, index) => {
                                    const statusStyle = getStatusStyle(item.status);
                                    return (
                                        <tr 
                                            key={item.id} 
                                            className="hover:bg-blue-50/50 transition-all duration-200 group"
                                            style={{ animationDelay: `${index * 50}ms` }}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md group-hover:scale-110 transition-transform">
                                                        {item.firstName?.charAt(0) || '?'}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-slate-800">
                                                            {item.firstName} {item.surname}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-slate-500">
                                                            <FaEnvelope className="text-blue-400" />
                                                            {item.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-medium text-slate-700 bg-slate-100 px-3 py-1 rounded-full">
                                                    {item.taluka}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                                    <FaPhone className="text-green-500 text-xs" />
                                                    {item.whatsappNo}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-sm">
                                                    {item.technicalQualification}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                                    <FaCalendarAlt className="text-purple-500" />
                                                    {item.submittedAt ? new Date(item.submittedAt).toLocaleDateString('mr-IN', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    }) : '-'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
                                                    <span>{statusStyle.icon}</span>
                                                    {item.status === 'accepted' ? 'स्वीकारले' : 
                                                     item.status === 'rejected' ? 'नाकारले' : 'प्रलंबित'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <button 
                                                    onClick={() => {
                                                        setSelectedItem(item);
                                                        setIsModalOpen(true);
                                                    }}
                                                    className="relative group/btn inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-5 py-2 rounded-xl text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                                                >
                                                    <span>तपशील पहा</span>
                                                    <span className="group-hover/btn:translate-x-1 transition-transform">→</span>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {filteredData.length === 0 && (
                        <div className="text-center py-16 px-4">
                            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaUserTie className="text-4xl text-slate-400" />
                            </div>
                            <p className="text-slate-600 font-medium">कोणतीही नोंदणी आढळली नाही</p>
                            <p className="text-sm text-slate-400 mt-1">कृपया शोध अटी बदलून पुन्हा प्रयत्न करा</p>
                        </div>
                    )}
                </div>

                <DetailModal 
                    isOpen={isModalOpen} 
                    onClose={() => setIsModalOpen(false)} 
                    data={selectedItem} 
                    title="सु.वे. अभियंता नोंदणी"
                    onStatusUpdate={handleStatusUpdate}
                    registrations={registrations}
                    showActions={false}
            />
        </div>
    );
};

export default EngineerRegistrations;
